import React, { useEffect, useMemo, useState } from "react";
import logo from "../assets/3d-logo.png";

type CourseSubItem = {
  title: string;
  desc: string;
  href: string;
  tag?: string;
};

type CourseCategory = {
  id: string;
  title: string;
  desc: string;
  courses: CourseSubItem[];
};

const courseCategories: CourseCategory[] = [
  {
    id: "class-7",
    title: "Class 7",
    desc: "Foundation courses for Class 7 students",
    courses: [
      {
        title: "Maths Foundation",
        desc: "Build strong maths basics with practice.",
        href: "/courses/class-7/maths",
      },
      {
        title: "Science Foundation",
        desc: "Physics, Chemistry and Biology basics.",
        href: "/courses/class-7/science",
      },
      {
        title: "Olympiad Practice",
        desc: "Reasoning and advanced school-level questions.",
        href: "/courses/class-7/olympiad",
      },
    ],
  },
  {
    id: "class-8",
    title: "Class 8",
    desc: "Strong base for competitive preparation",
    courses: [
      {
        title: "Maths Mastery",
        desc: "Chapter-wise concepts and practice.",
        href: "/courses/class-8/maths",
      },
      {
        title: "Science Mastery",
        desc: "Complete science learning with tests.",
        href: "/courses/class-8/science",
      },
      {
        title: "Reasoning Batch",
        desc: "Logical and analytical skill building.",
        href: "/courses/class-8/reasoning",
      },
    ],
  },
  {
    id: "class-9",
    title: "Class 9",
    desc: "NCERT plus JEE / NEET foundation",
    courses: [
      {
        title: "Class 9 Foundation",
        desc: "Maths and Science complete syllabus.",
        href: "/courses/class-9/foundation",
      },
      {
        title: "JEE Foundation",
        desc: "Early preparation for IIT-JEE.",
        href: "/courses/class-9/jee-foundation",
        tag: "JEE",
      },
      {
        title: "NEET Foundation",
        desc: "Early preparation for medical entrance.",
        href: "/courses/class-9/neet-foundation",
        tag: "NEET",
      },
    ],
  },
  {
    id: "class-10",
    title: "Class 10",
    desc: "Boards plus competitive foundation",
    courses: [
      {
        title: "Board Exam Batch",
        desc: "NCERT, sample papers and tests.",
        href: "/courses/class-10/boards",
      },
      {
        title: "JEE Foundation",
        desc: "Advanced Maths and Science base.",
        href: "/courses/class-10/jee-foundation",
        tag: "JEE",
      },
      {
        title: "NEET Foundation",
        desc: "Science and Biology focused learning.",
        href: "/courses/class-10/neet-foundation",
        tag: "NEET",
      },
    ],
  },
  {
    id: "class-11",
    title: "Class 11",
    desc: "JEE, NEET and Boards preparation",
    courses: [
      {
        title: "JEE Main + Advanced",
        desc: "Physics, Chemistry and Maths.",
        href: "/courses/class-11/jee",
        tag: "JEE",
      },
      {
        title: "NEET UG",
        desc: "Physics, Chemistry and Biology.",
        href: "/courses/class-11/neet",
        tag: "NEET",
      },
      {
        title: "Boards + Competitive",
        desc: "School and entrance preparation together.",
        href: "/courses/class-11/boards-competitive",
      },
    ],
  },
  {
    id: "class-12",
    title: "Class 12",
    desc: "Final year JEE / NEET preparation",
    courses: [
      {
        title: "JEE Main Batch",
        desc: "Complete JEE Main syllabus.",
        href: "/courses/class-12/jee-main",
        tag: "JEE Main",
      },
      {
        title: "JEE Advanced Batch",
        desc: "Advanced-level problem solving.",
        href: "/courses/class-12/jee-advanced",
        tag: "Advanced",
      },
      {
        title: "NEET Target Batch",
        desc: "NCERT-based medical preparation.",
        href: "/courses/class-12/neet",
        tag: "NEET",
      },
    ],
  },
  {
    id: "dropper",
    title: "Dropper Batch",
    desc: "Repeaters special preparation",
    courses: [
      {
        title: "JEE Dropper",
        desc: "Complete yearly preparation for JEE.",
        href: "/courses/dropper/jee",
        tag: "JEE",
      },
      {
        title: "NEET Dropper",
        desc: "Complete yearly preparation for NEET.",
        href: "/courses/dropper/neet",
        tag: "NEET",
      },
      {
        title: "Crash Course",
        desc: "Fast revision with mock tests.",
        href: "/courses/dropper/crash-course",
      },
    ],
  },
  {
    id: "test-series",
    title: "Test Series",
    desc: "Practice with real exam pattern",
    courses: [
      {
        title: "JEE Mains Test Series",
        desc: "Chapter-wise and full-length mock tests.",
        href: "/test-series/jee-mains",
      },
      {
        title: "NEET Test Series",
        desc: "NCERT-based full mock tests.",
        href: "/test-series/neet",
      },
      {
        title: "Board Test Series",
        desc: "School exam preparation tests.",
        href: "/test-series/boards",
      },
    ],
  },
];

const navLinks = [
  { label: "Live Batches", href: "/live-batches" },
  { label: "LMS Skills", href: "/lms-skills" },
  { label: "Real Test", href: "/real-test" },
  { label: "Our Mentors", href: "/our-mentors" },
  { label: "About", href: "/about" },
];

const formatTime = (totalSeconds: number) => {
  const days = Math.floor(totalSeconds / (60 * 60 * 24));
  const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = totalSeconds % 60;

  return {
    days: String(days).padStart(2, "0"),
    hours: String(hours).padStart(2, "0"),
    minutes: String(minutes).padStart(2, "0"),
    seconds: String(seconds).padStart(2, "0"),
  };
};

const getStudentName = () => {
  try {
    const studentData = localStorage.getItem("student");
    const userData = localStorage.getItem("user");

    const parsedStudent = studentData ? JSON.parse(studentData) : null;
    const parsedUser = userData ? JSON.parse(userData) : null;

    return (
      parsedStudent?.fullName ||
      parsedStudent?.name ||
      parsedUser?.fullName ||
      parsedUser?.name ||
      localStorage.getItem("studentName") ||
      localStorage.getItem("name") ||
      ""
    );
  } catch {
    return (
      localStorage.getItem("studentName") ||
      localStorage.getItem("name") ||
      ""
    );
  }
};

const AllCoursesMegaMenu = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredCategories = useMemo(() => {
    const value = searchTerm.trim().toLowerCase();

    if (!value) return courseCategories;

    return courseCategories
      .map((category) => {
        const categoryMatched =
          category.title.toLowerCase().includes(value) ||
          category.desc.toLowerCase().includes(value);

        const matchedCourses = category.courses.filter(
          (course) =>
            course.title.toLowerCase().includes(value) ||
            course.desc.toLowerCase().includes(value) ||
            course.tag?.toLowerCase().includes(value)
        );

        if (categoryMatched) return category;

        return matchedCourses.length
          ? { ...category, courses: matchedCourses }
          : null;
      })
      .filter(Boolean) as CourseCategory[];
  }, [searchTerm]);

  const safeActiveIndex =
    activeIndex >= filteredCategories.length ? 0 : activeIndex;

  const activeCategory =
    filteredCategories[safeActiveIndex] || filteredCategories[0];

  return (
    <div
      className="
        invisible fixed left-0 top-[118px] z-[95] w-full translate-y-2
        border-t border-slate-200 bg-white opacity-0 shadow-[0_18px_48px_rgba(15,23,42,0.13)]
        transition-all duration-300 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100
        sm:top-[110px] 
      "
    >
      <div className="mx-auto flex max-w-[1420px]">
        <aside className="w-[310px] shrink-0 border-r border-slate-200 bg-white px-4 py-4">
          <div className="mb-4">
            <div className="flex h-11 items-center gap-2 rounded-md border border-slate-300 bg-white px-3 transition-all focus-within:border-blue-500">
              <svg
                className="h-4 w-4 text-slate-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"
                />
              </svg>

              <input
                value={searchTerm}
                onChange={(event) => {
                  setSearchTerm(event.target.value);
                  setActiveIndex(0);
                }}
                placeholder="I'm looking for..."
                className="h-full w-full bg-transparent text-[14px] font-normal text-slate-900 outline-none placeholder:text-slate-400"
              />
            </div>
          </div>

          <div className="mb-3 flex items-center justify-between border-b border-slate-200 pb-3">
            <button className="border-b-2 border-red-500 pb-2 text-[15px] font-medium text-[#0067b1]">
              All Courses
            </button>

            <a href="/all-classes" className="text-sm font-medium text-blue-600">
              Explore all
            </a>
          </div>

          <div className="max-h-[430px] overflow-y-auto pr-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {filteredCategories.length > 0 ? (
              filteredCategories.map((category, index) => (
                <button
                  key={category.id}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => {
                    setActiveIndex(index);
                    window.location.href = `/all-classes?class=${category.id}`;
                  }}
                  className={`flex w-full items-center justify-between rounded-md px-3 py-2.5 text-left transition-all ${
                    safeActiveIndex === index
                      ? "bg-[#eef6ff] text-[#0067b1]"
                      : "text-[#111827] hover:bg-slate-50"
                  }`}
                >
                  <div className="min-w-0">
                    <p className="truncate text-[15px] font-normal leading-5">
                      {category.title}
                    </p>
                    <p className="mt-0.5 line-clamp-1 text-[12px] font-normal leading-5 text-slate-500">
                      {category.desc}
                    </p>
                  </div>

                  <span
                    className={`ml-3 text-xl leading-none ${
                      safeActiveIndex === index
                        ? "text-[#0067b1]"
                        : "text-slate-300"
                    }`}
                  >
                    ›
                  </span>
                </button>
              ))
            ) : (
              <div className="rounded-md bg-slate-50 p-4 text-[14px] text-slate-500">
                No course found.
              </div>
            )}
          </div>
        </aside>

        <section className="min-h-[530px] flex-1 bg-white px-7 py-5">
          {activeCategory ? (
            <>
              <div className="mb-5 flex items-center justify-between gap-4">
                <div>
                  <p className="text-[13px] font-medium uppercase tracking-[0.12em] text-slate-500">
                    {activeCategory.title}
                  </p>

                  <h2 className="mt-1 text-[28px] font-normal leading-tight text-[#111827]">
                    {activeCategory.desc}
                  </h2>
                </div>

                <a
                  href={`/all-classes?class=${activeCategory.id}`}
                  className="hidden rounded-md border border-slate-300 px-4 py-2 text-[14px] font-normal text-slate-800 transition-all hover:border-[#0067b1] hover:text-[#0067b1] md:inline-flex"
                >
                  View Category
                </a>
              </div>

              <div className="grid grid-cols-3 gap-4">
                {activeCategory.courses.map((course) => (
                  <a
                    key={course.href}
                    href={`/all-classes?class=${activeCategory.id}`}
                    className="
                      min-h-[170px] rounded-md border border-slate-200 bg-[#f7fbff] p-5
                      transition-all hover:border-[#0067b1] hover:bg-white hover:shadow-[0_8px_24px_rgba(15,23,42,0.08)]
                    "
                  >
                    <div className="mb-4 flex items-start justify-between gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-md bg-white shadow-sm">
                        <span className="text-[20px] text-[#0067b1]">✦</span>
                      </div>

                      {course.tag && (
                        <span className="rounded-full bg-blue-100 px-2.5 py-1 text-[11px] font-medium text-[#0067b1]">
                          {course.tag}
                        </span>
                      )}
                    </div>

                    <h4 className="text-[20px] font-medium leading-snug text-[#111827]">
                      {course.title}
                    </h4>

                    <p className="mt-2 text-[15px] font-normal leading-6 text-slate-600">
                      {course.desc}
                    </p>

                    <p className="mt-5 text-[14px] font-medium text-[#0067b1]">
                      Start Learning ›
                    </p>
                  </a>
                ))}
              </div>

              <div className="mt-5 rounded-md border border-slate-200 bg-white p-5">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h4 className="text-[20px] font-normal text-[#111827]">
                      Need help choosing the right course?
                    </h4>
                    <p className="mt-1 text-[15px] font-normal text-slate-600">
                      Talk to our academic counselor and get the best roadmap.
                    </p>
                  </div>

                  <a
                    href="/contact"
                    className="rounded-md bg-[#07111b] px-5 py-3 text-center text-[14px] font-medium text-white transition-all hover:bg-[#111827]"
                  >
                    Talk to Counselor
                  </a>
                </div>
              </div>
            </>
          ) : (
            <div className="flex h-full items-center justify-center text-[15px] text-slate-500">
              Search course from left side.
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

const Navbar: React.FC = () => {
  const OFFER_SECONDS = 2 * 24 * 60 * 60 + 14 * 60 * 60 + 52 * 60 + 16;

  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [mobileCoursesOpen, setMobileCoursesOpen] = useState(false);
  const [mobileActiveCourse, setMobileActiveCourse] = useState<number | null>(0);
  const [remainingSeconds, setRemainingSeconds] = useState(OFFER_SECONDS);

  const [studentName, setStudentName] = useState<string>("");

  useEffect(() => {
    const syncUser = () => {
      const token = localStorage.getItem("token");
      const name = getStudentName();

      if (token && name) {
        setStudentName(name);
      } else {
        setStudentName("");
      }
    };

    syncUser();

    window.addEventListener("storage", syncUser);

    return () => window.removeEventListener("storage", syncUser);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("student");
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("studentId");
    localStorage.removeItem("userId");
    localStorage.removeItem("id");
    localStorage.removeItem("studentName");
    localStorage.removeItem("name");
    localStorage.removeItem("email");
    localStorage.removeItem("mobile");
    localStorage.removeItem("phone");

    setStudentName("");
    window.location.href = "/";
  };

  useEffect(() => {
    const storageKey = "lms_offer_countdown_end_time";
    const savedEndTime = localStorage.getItem(storageKey);
    const now = Date.now();

    let endTime = savedEndTime ? Number(savedEndTime) : 0;

    if (!endTime || endTime <= now) {
      endTime = now + OFFER_SECONDS * 1000;
      localStorage.setItem(storageKey, String(endTime));
    }

    const updateCountdown = () => {
      const diff = Math.max(0, Math.floor((endTime - Date.now()) / 1000));
      setRemainingSeconds(diff);

      if (diff <= 0) {
        const newEndTime = Date.now() + OFFER_SECONDS * 1000;
        localStorage.setItem(storageKey, String(newEndTime));
        endTime = newEndTime;
      }
    };

    updateCountdown();

    const timer = window.setInterval(updateCountdown, 1000);
    return () => window.clearInterval(timer);
  }, []);

  const time = formatTime(remainingSeconds);

  const goToLogin = () => {
    window.location.href = "/login";
  };

  const goToDashboard = () => {
    window.location.href = "/student-dashboard";
  };

  const profileInitial = studentName ? studentName.charAt(0).toUpperCase() : "S";

  return (
    <div
      className="font-sans"
      style={{
        fontFamily: '"Zoho Puvi", Inter, Arial, Helvetica, sans-serif',
      }}
    >
      {/* Top Offer Bar */}
      <div className="fixed left-0 top-0 z-[100] h-[54px] w-full bg-gradient-to-r from-[#07111b] via-[#17172f] to-[#3b2418] px-2 text-white sm:h-[46px]">
        <div className="mx-auto flex h-full max-w-[1400px] flex-wrap items-center justify-center gap-x-2 gap-y-0 text-center sm:gap-x-5">
          <span className="rounded bg-blue-500 px-2.5 py-0.5 text-[10px] font-medium shadow-sm sm:px-3 sm:py-1 sm:text-xs">
            Exclusive Offer
          </span>

          <p className="text-[10px] font-medium leading-none sm:text-sm md:text-base">
            Upgrade your skills with up to 50% savings
          </p>

          <p className="font-mono text-[11px] font-semibold leading-none tracking-wider sm:text-lg md:text-xl">
            {time.days}D : {time.hours}H : {time.minutes}M : {time.seconds}S
          </p>

          <a
            href="/offer"
            className="rounded border border-white/30 px-2.5 py-1 text-[10px] font-medium leading-none text-white/90 transition-all hover:bg-white/10 sm:px-4 sm:py-1.5 sm:text-sm"
          >
            Start Learning Today ↗
          </a>
        </div>
      </div>

      {/* Main Navbar */}
      <nav className="fixed left-0 top-[54px] z-[90] h-[64px] w-full border-b border-slate-200 bg-white shadow-sm sm:top-[46px]">
        <div className="mx-auto flex h-full max-w-[1400px] items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-9 lg:gap-10 xl:-ml-8 2xl:-ml-12">
            <a href="/" className="flex shrink-0 items-center">
              <img
                src={logo}
                alt="LMS Logo"
                className="h-10 w-auto object-contain sm:h-12 md:h-14"
              />
            </a>

            <div className="group relative hidden lg:block">
              <button className="flex items-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2.5 text-[14px] font-bold text-slate-900 transition-all hover:border-[#0067b1] hover:text-[#0067b1]">
                All Courses
                <svg
                  className="h-4 w-4 transition-transform duration-300 group-hover:rotate-180"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.1}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              <AllCoursesMegaMenu />
            </div>
          </div>

          <div className="hidden items-center gap-8 lg:flex">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-[16px] font-medium text-[#111827] transition-all hover:text-[#0067b1]"
              >
                {link.label}
              </a>
            ))}
          </div>

          {studentName ? (
            <div className="hidden items-center gap-3 sm:flex">
              <button
                onClick={goToDashboard}
                className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-4 py-2.5 text-[15px] font-bold text-[#07111b] shadow-sm transition-all hover:bg-slate-100 hover:text-blue-700"
              >
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-xs font-black text-white">
                  {profileInitial}
                </div>
                {studentName}
              </button>

              <button
                onClick={handleLogout}
                className="rounded-md border border-red-100 bg-red-50 px-4 py-2.5 text-[14px] font-bold text-red-600 transition-all hover:bg-red-100"
              >
                Logout
              </button>
            </div>
          ) : (
            <button
              onClick={goToLogin}
              className="hidden rounded-md bg-[#07111b] px-6 py-2.5 text-[15px] font-medium text-white transition-transform hover:-translate-y-0.5 hover:shadow-lg sm:block"
            >
              Login / Register
            </button>
          )}

          <button
            onClick={() => setIsMobileOpen((prev) => !prev)}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-900 lg:hidden"
            aria-label="Toggle menu"
          >
            {isMobileOpen ? (
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.1}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : (
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.1}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileOpen && (
          <div className="max-h-[calc(100vh-118px)] overflow-y-auto border-t border-slate-100 bg-white px-4 py-4 shadow-xl sm:max-h-[calc(100vh-110px)] lg:hidden">
            <button
              onClick={() => setMobileCoursesOpen((prev) => !prev)}
              className="flex w-full items-center justify-between rounded-md bg-slate-50 px-4 py-3 text-left text-[15px] font-medium text-slate-900"
            >
              All Courses
              <span
                className={`transition-transform ${
                  mobileCoursesOpen ? "rotate-180" : ""
                }`}
              >
                ↓
              </span>
            </button>

            {mobileCoursesOpen && (
              <div className="mt-3 space-y-3">
                {courseCategories.map((category, index) => (
                  <div
                    key={category.id}
                    className="rounded-md border border-slate-200 bg-white"
                  >
                    <button
                      onClick={() =>
                        setMobileActiveCourse(
                          mobileActiveCourse === index ? null : index
                        )
                      }
                      className="flex w-full items-center justify-between gap-3 p-4 text-left"
                    >
                      <div>
                        <p className="text-[15px] font-medium text-slate-950">
                          {category.title}
                        </p>
                        <p className="mt-1 text-[13px] font-normal text-slate-500">
                          {category.desc}
                        </p>
                      </div>

                      <span
                        className={`shrink-0 transition-transform ${
                          mobileActiveCourse === index ? "rotate-180" : ""
                        }`}
                      >
                        ↓
                      </span>
                    </button>

                    {mobileActiveCourse === index && (
                      <div className="space-y-2 border-t border-slate-100 p-3">
                        {category.courses.map((course) => (
                          <a
                            key={course.href}
                            href={`/all-classes?class=${category.id}`}
                            onClick={() => setIsMobileOpen(false)}
                            className="block rounded-md bg-slate-50 px-4 py-3"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-[14px] font-medium text-slate-900">
                                {course.title}
                              </p>

                              {course.tag && (
                                <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-[#0067b1]">
                                  {course.tag}
                                </span>
                              )}
                            </div>

                            <p className="mt-1 text-[12px] font-normal text-slate-500">
                              {course.desc}
                            </p>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="mt-4 space-y-1">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setIsMobileOpen(false)}
                  className="block rounded-md px-4 py-3 text-[15px] font-medium text-slate-900 hover:bg-slate-50"
                >
                  {link.label}
                </a>
              ))}
            </div>

            {studentName ? (
              <div className="mt-4 space-y-3 border-t border-slate-200 pt-4">
                <button
                  onClick={() => {
                    setIsMobileOpen(false);
                    goToDashboard();
                  }}
                  className="flex w-full items-center gap-3 rounded-md bg-slate-50 px-3 py-3 text-left hover:bg-slate-100"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-base font-black text-white shadow-sm">
                    {profileInitial}
                  </div>

                  <div>
                    <p className="text-[15px] font-bold text-slate-900">
                      {studentName}
                    </p>
                    <p className="text-[12px] font-medium text-slate-500">
                      Open Student Dashboard
                    </p>
                  </div>
                </button>

                <button
                  onClick={handleLogout}
                  className="block w-full rounded-md bg-red-50 px-4 py-3 text-left text-[15px] font-bold text-red-600 hover:bg-red-100"
                >
                  Logout
                </button>
              </div>
            ) : (
              <button
                onClick={goToLogin}
                className="mt-4 w-full rounded-md bg-[#07111b] py-3 text-[15px] font-medium text-white"
              >
                Login / Register
              </button>
            )}
          </div>
        )}
      </nav>

      <div className="h-[118px] sm:h-[110px]" />
    </div>
  );
};

export default Navbar;